import { FC, ReactNode, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../../../context/searchContext";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { useNormativesApi } from "../../../../../http/patrol/useNormatives";
import FlexContent from "../../../../ui/FlexContent";
import Header, { HeaderPath } from "../../../../ui/Header";
import Tabs, { Tab } from "../../../../ui/Tabs";
import { PATROL } from "../../AdminSystemSettings";
import ExcludedItems from "./ExcludedItems";
import NormativeDetails from "./NormativeDetails";
import NormativeVersions from "./NormativeVersions";

const Normative: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { normativeId } = useParams<{ normativeId: string }>();
  const normativesApi = useNormativesApi();
  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("tab")) || 0;

  const aborter = useRef(new AbortController());
  const { error, data, isMutating, trigger } = normativesApi.getAdminNormative(aborter.current.signal);

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: PATROL },
    {
      url: `/admin/systems/${systemId}/settings?tab=1`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.normatives.plural}`,
    },
    { display: data?.name ?? "" },
  ];

  useEffect(() => {
    normativeId && trigger(normativeId);
  }, [normativeId]);

  const tabs: Tab[] = [
    { title: i18n.translation.common.generalData, hasSubTabs: true },
    { title: i18n.translation.common.versions },
    { title: i18n.translation.common.excludedItems },
  ];

  const contents: ReactNode[] = [
    <FlexContent padding direction="row">
      <NormativeDetails normative={!error ? data : undefined} disabled={isMutating || !!error} />
    </FlexContent>,
    <NormativeVersions active={selectedIndex === 1} />,
    <ExcludedItems active={selectedIndex === 2} />,
  ];

  return (
    <SearchContextProvider>
      <NormativeContent
        title={`${i18n.translation.normatives.singular}: ${data?.name ?? ""}`}
        error={error}
        path={path}
        tabs={tabs}
        contents={contents}
      />
    </SearchContextProvider>
  );
};

export default Normative;

type NormativeContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  tabs: Tab[];
  contents: ReactNode[];
};

const NormativeContent: FC<NormativeContentProps> = ({ title, error, path, tabs, contents }) => {
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header title={tabTitle ? `${title} · ${tabTitle}` : title} error={error} path={path} buttons={actions} />
      <Tabs urlParameter="tab" tabs={tabs} contents={contents} />
    </>
  );
};
